<script setup lang="ts">
import { CheckIcon, SearchIcon } from "lucide-vue-next";
import { computed } from "vue";

import type { MentionableUser } from "@/types";

interface Props {
  users: MentionableUser[];
  selectedIds?: number[];
  search?: string;
}

const props = withDefaults(defineProps<Props>(), {
  selectedIds: () => [],
  search: "",
});

const emit = defineEmits<{
  "update:selectedIds": [value: number[]];
  "update:search": [value: string];
}>();

const normalizedSearch = computed(() => props.search.toLowerCase().trim());

const filteredUsers = computed(() => {
  if (!normalizedSearch.value) return props.users;

  return props.users.filter(
    (user) =>
      user.name.toLowerCase().includes(normalizedSearch.value) ||
      user.email.toLowerCase().includes(normalizedSearch.value),
  );
});

const selectedSet = computed(() => new Set(props.selectedIds));

const updateSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:search", target.value);
};

const toggleUser = (userId: number) => {
  const next = new Set(selectedSet.value);
  if (next.has(userId)) next.delete(userId);
  else next.add(userId);

  emit("update:selectedIds", Array.from(next));
};
</script>

<template>
  <div class="overflow-hidden rounded-md border">
    <div class="flex items-center gap-2 border-b px-3 py-1.5">
      <SearchIcon class="size-3.5 shrink-0 text-muted-foreground" />
      <input
        :value="search"
        type="text"
        class="h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        placeholder="Buscar usuario…"
        @input="updateSearch"
      >
    </div>
    <div class="max-h-48 divide-y divide-border overflow-y-auto">
      <label
        v-for="user in filteredUsers"
        :key="user.id"
        class="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-muted/50"
        :class="{ 'bg-primary/5': selectedSet.has(user.id) }"
        @click.prevent="toggleUser(user.id)"
      >
        <span
          class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors"
          :class="
            selectedSet.has(user.id)
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input'
          "
        >
          <CheckIcon v-if="selectedSet.has(user.id)" class="size-3" />
        </span>
        <span class="truncate">{{ user.name }}</span>
      </label>
      <p v-if="filteredUsers.length === 0" class="px-3 py-3 text-center text-xs text-muted-foreground">
        Sin resultados
      </p>
    </div>
  </div>
</template>
